import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Copy,
  Loader2,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router';
import {
  PageAction,
  PageContent,
  PageDescription,
  PageHeader,
  PageHeaderInfo,
  PageTitle,
} from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableHead,
  StyledTableHeader,
  StyledTableRow,
  StyledTableWrapper,
} from '@/components/ui/styled-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useListAddressBookEntries } from '@/hooks/organization/use-list-address-book';
import { useEditAddressBook } from '@/hooks/organization/use-edit-address-book';
import { useAutoLoadMore } from '@/hooks/use-auto-load-more';
import { formatAddress } from '@/lib/utils';
import { formatName } from '@/lib/name';
import { getRequestErrorMessage } from '@/lib/api/get-request-error-message';
import { NewAddressBookEntryDialog } from '@/components/address-book/new-address-book-entry-dialog';
import { EditAddressBookEntryNameDialog } from '@/components/address-book/edit-address-book-entry-name-dialog';
import { ImportAddressBookCsvDialog } from '@/components/address-book/import-address-book-csv-dialog';
import type { AddressBookListEntry } from '@/types/address-book';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import {
  ReorderableList,
  SortableItem,
} from '@/components/shared/reorderable-list';
import { ReorderError } from '@/components/shared/reorder-error';
import { useOrganizationItemOrder } from '@/hooks/admin/organization/use-organization-item-order';

const getAddressBookEntryId = (entry: AddressBookListEntry) => entry.address;

function getAddressBookEntryTypeLabel(
  type: AddressBookListEntry['type']
): string | null {
  switch (type) {
    case 'MLSACCOUNT':
      return 'MLS Account';
    case 'EOA':
      return 'Wallet (EOA)';
    case 'CONTRACT':
      return 'Contract';
    default:
      return null;
  }
}

function formatLastUpdated(updatedAt: string, userName: string) {
  return `${formatDistanceToNow(new Date(updatedAt), {
    addSuffix: true,
  })} by ${formatName(userName)}`;
}

/**
 * Address book listing page for saved organization addresses.
 *
 * Networks are intentionally ignored here because the underlying address book
 * is org-scoped and network-agnostic.
 */
export function AddressBookPage() {
  const { organization } = useMyOrganization();
  const isAdmin = organization?.isAdmin ?? false;
  const [searchQuery, setSearchQuery] = useState('');
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [selectedEntryForRename, setSelectedEntryForRename] =
    useState<AddressBookListEntry | null>(null);
  const [selectedEntryForDelete, setSelectedEntryForDelete] =
    useState<AddressBookListEntry | null>(null);
  const [mlsAccountDialogState, setMlsAccountDialogState] = useState<{
    entry: AddressBookListEntry;
    action: 'rename' | 'delete';
  } | null>(null);
  const [openDropdownAddress, setOpenDropdownAddress] = useState<string | null>(
    null
  );
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(
    null
  );
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const normalizedSearchQuery = useMemo(
    () => searchQuery.trim(),
    [searchQuery]
  );
  const {
    entries,
    pagination,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore,
    refresh,
  } = useListAddressBookEntries({
    ...(normalizedSearchQuery.length > 0
      ? { query: normalizedSearchQuery }
      : {}),
    pagination: { limit: 20 },
    refreshInterval: 10_000,
  });
  const {
    orderedItems: orderedEntries,
    orderedIds: orderedEntryIds,
    reorder: reorderEntries,
    isReordering,
    reorderError,
  } = useOrganizationItemOrder({
    items: entries,
    itemType: 'addressBookEntries',
    getItemId: getAddressBookEntryId,
    enabled: isAdmin,
  });
  const { editAddressBook, isLoading: isDeleting } = useEditAddressBook();

  useEffect(() => {
    // Re-entering the route should load a fresh first page instead of showing
    // whatever paginated snapshot SWR still has cached from a prior visit.
    void refresh();
  }, [refresh]);

  useAutoLoadMore(
    loadMoreRef,
    true,
    hasMore,
    isLoadingMore,
    pagination.cursor,
    loadMore
  );

  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
    } catch {
      // Clipboard failures are non-fatal for the page.
    }
  };

  const handleDelete = async () => {
    if (!selectedEntryForDelete) {
      return;
    }

    setDeleteErrorMessage(null);

    try {
      await editAddressBook({
        instructions: [
          {
            action: 'delete',
            address: selectedEntryForDelete.address,
          },
        ],
      });
      await refresh();
      setSelectedEntryForDelete(null);
    } catch (error) {
      setDeleteErrorMessage(
        getRequestErrorMessage(error, 'Failed to delete address book entry.')
      );
    }
  };

  const handleRenameMenuClick = (entry: AddressBookListEntry) => {
    if (entry.type === 'MLSACCOUNT') {
      setMlsAccountDialogState({ entry, action: 'rename' });
      return;
    }

    setSelectedEntryForRename(entry);
  };

  const handleDeleteMenuClick = (entry: AddressBookListEntry) => {
    if (entry.type === 'MLSACCOUNT') {
      setMlsAccountDialogState({ entry, action: 'delete' });
      return;
    }

    setDeleteErrorMessage(null);
    setSelectedEntryForDelete(entry);
  };

  return (
    <div>
      <PageHeader>
        <PageHeaderInfo>
          <PageTitle>Address Book</PageTitle>
          <PageDescription>
            Manage saved EVM address labels for faster transaction setup.
          </PageDescription>
        </PageHeaderInfo>
        <PageAction>
          <div className="flex flex-col items-end gap-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsImportDialogOpen(true)}
              >
                <Upload className="mr-1 h-4 w-4" />
                Import CSV
              </Button>
              <Button onClick={() => setIsNewDialogOpen(true)}>
                <Plus className="mr-1 h-4 w-4" />
                New Address Label
              </Button>
            </div>
          </div>
        </PageAction>
      </PageHeader>

      <PageContent className="space-y-4">
        <div className="max-w-xl">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search addresses"
              className="rounded-full bg-background pl-11"
            />
          </div>
        </div>

        <ReorderError message={reorderError} />

        <StyledTableWrapper>
          <ReorderableList
            ids={orderedEntryIds}
            disabled={!isAdmin || isReordering || isLoadingMore}
            onReorder={reorderEntries}
          >
            <StyledTable className="table-fixed">
              <colgroup>
                {isAdmin ? <col className="w-10" /> : null}
                <col className="w-[31%]" />
                <col className="w-[12.5rem]" />
                <col className="w-[9rem]" />
                <col />
                <col className="w-[4rem]" />
              </colgroup>
              <StyledTableHeader>
                <tr>
                  {isAdmin ? (
                    <StyledTableHead className="w-10 px-1 first:pl-1" />
                  ) : null}
                  <StyledTableHead className={isAdmin ? 'pl-0' : undefined}>
                    Name
                  </StyledTableHead>
                  <StyledTableHead>Address</StyledTableHead>
                  <StyledTableHead className="whitespace-nowrap">
                    Type
                  </StyledTableHead>
                  <StyledTableHead className="pl-6">
                    Last Updated
                  </StyledTableHead>
                  <StyledTableHead className="w-0" />
                </tr>
              </StyledTableHeader>
              <StyledTableBody>
                {isLoading ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={isAdmin ? 6 : 5}>
                      <div className="flex h-48 items-center justify-center text-slate-500">
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Loading address book...
                      </div>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : orderedEntries.length === 0 ? (
                  <StyledTableRow>
                    <StyledTableCell colSpan={isAdmin ? 6 : 5}>
                      <div className="flex h-48 items-center justify-center text-slate-500">
                        {searchQuery.trim().length > 0
                          ? `No addresses found matching "${searchQuery}".`
                          : 'No saved addresses yet.'}
                      </div>
                    </StyledTableCell>
                  </StyledTableRow>
                ) : (
                  <>
                    {orderedEntries.map((entry) => (
                      <SortableItem
                        key={entry.address}
                        id={entry.address}
                        label={entry.name}
                        disabled={!isAdmin || isReordering || isLoadingMore}
                      >
                        {({ setNodeRef, style, dragHandle }) => (
                          <StyledTableRow
                            ref={setNodeRef}
                            style={style}
                            className="hover:bg-muted/50"
                          >
                            {isAdmin ? (
                              <StyledTableCell className="w-10 px-1 first:pl-1 py-2 text-center">
                                {dragHandle}
                              </StyledTableCell>
                            ) : null}
                            <StyledTableCell
                              className={isAdmin ? 'pl-0' : undefined}
                            >
                              <div className="truncate text-base font-semibold">
                                {entry.name}
                              </div>
                            </StyledTableCell>
                            <StyledTableCell>
                              <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
                                <span className="truncate font-mono text-sm">
                                  {formatAddress(entry.address)}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() =>
                                    void handleCopyAddress(entry.address)
                                  }
                                >
                                  <Copy className="h-3.5 w-3.5" />
                                  <span className="sr-only">Copy address</span>
                                </Button>
                              </div>
                            </StyledTableCell>
                            <StyledTableCell>
                              {getAddressBookEntryTypeLabel(entry.type) ? (
                                <Badge
                                  variant="secondary"
                                  className="max-w-full whitespace-nowrap font-medium"
                                >
                                  {getAddressBookEntryTypeLabel(entry.type)}
                                </Badge>
                              ) : null}
                            </StyledTableCell>
                            <StyledTableCell className="pl-6 text-muted-foreground">
                              <div className="truncate">
                                {formatLastUpdated(
                                  entry.updatedAt,
                                  entry.lastUpdatedByUserName
                                )}
                              </div>
                            </StyledTableCell>
                            <StyledTableCell className="text-right">
                              <DropdownMenu
                                open={openDropdownAddress === entry.address}
                                onOpenChange={(open) =>
                                  setOpenDropdownAddress(
                                    open ? entry.address : null
                                  )
                                }
                              >
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 data-[state=open]:bg-muted"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Open menu</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="w-48 z-[9999]"
                                >
                                  <DropdownMenuItem
                                    onClick={() => handleRenameMenuClick(entry)}
                                  >
                                    Rename
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => handleDeleteMenuClick(entry)}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </StyledTableCell>
                          </StyledTableRow>
                        )}
                      </SortableItem>
                    ))}
                  </>
                )}
              </StyledTableBody>
            </StyledTable>
          </ReorderableList>
        </StyledTableWrapper>

        {hasMore || isLoadingMore ? (
          <div
            ref={loadMoreRef}
            className="flex min-h-12 items-center justify-center py-4"
          >
            {isLoadingMore ? (
              <Loader2
                className="h-5 w-5 animate-spin text-muted-foreground"
                aria-label="Loading more address book entries"
              />
            ) : null}
          </div>
        ) : null}
      </PageContent>

      <ImportAddressBookCsvDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onImported={() => {
          void refresh();
        }}
      />

      <NewAddressBookEntryDialog
        open={isNewDialogOpen}
        onOpenChange={setIsNewDialogOpen}
        onCreated={() => {
          void refresh();
        }}
      />

      <EditAddressBookEntryNameDialog
        open={selectedEntryForRename !== null}
        entry={selectedEntryForRename}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedEntryForRename(null);
          }
        }}
        onUpdated={() => {
          void refresh();
        }}
      />

      <Dialog
        open={mlsAccountDialogState !== null}
        onOpenChange={(open) => {
          if (!open) {
            setMlsAccountDialogState(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {mlsAccountDialogState?.action === 'delete'
                ? 'MLS accounts cannot be deleted'
                : 'Manage this from Accounts'}
            </DialogTitle>
            <DialogDescription>
              {mlsAccountDialogState?.action === 'delete' ? (
                <>
                  MLS account address book entries are synced from accounts and
                  cannot be deleted from the address book.
                </>
              ) : (
                <>
                  MLS account address book entries are synced from account
                  names. To rename{' '}
                  <span className="font-medium text-foreground">
                    {mlsAccountDialogState?.entry.name}
                  </span>
                  , head to the Accounts page and change the account name there.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMlsAccountDialogState(null)}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setMlsAccountDialogState(null);
                navigate('/accounts');
              }}
            >
              Go to Accounts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={selectedEntryForDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteErrorMessage(null);
            setSelectedEntryForDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete address label</DialogTitle>
            <DialogDescription>
              Remove{' '}
              <span className="font-medium text-foreground">
                {selectedEntryForDelete?.name}
              </span>{' '}
              from the address book.
            </DialogDescription>
          </DialogHeader>

          {deleteErrorMessage ? (
            <div className="text-sm text-rose-600">{deleteErrorMessage}</div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteErrorMessage(null);
                setSelectedEntryForDelete(null);
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={isDeleting}
              className="relative"
            >
              <span className={isDeleting ? 'invisible' : ''}>Delete</span>
              {isDeleting ? (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              ) : null}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
